import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { camelToSnakeCase } from 'utils/caseConversion';
import { useEpaFlag } from 'utils/featureFlags';
import {
  EPA_GULF_SYMBOLOGY,
  epaGulfResourceNotesMarker,
  formatEpaFacilityLabel,
  isEpaGulfResourceSet,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/epaGulfThreatNotification';
import {
  keys as resourceSetKeys,
  mapResourceSetToEditPayload,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import { queryNplSitesInHeleneCone } from './queryNplSitesInHeleneCone';

const EPA_RESOURCE_KIND = 'Facilities';
const EPA_RESOURCE_TYPE = 'Superfund Site';

const ensureLocks = new Map<string, Promise<unknown>>();

async function patchMissingEpaSymbology({
  resources,
  groupGuid,
  userGuid,
}: {
  resources: ResourceSet[];
  groupGuid: string;
  userGuid: string;
}): Promise<boolean> {
  const missing = resources.filter(
    (resource) => isEpaGulfResourceSet(resource) && !resource.symbology
  );
  if (missing.length === 0) return false;

  await Promise.all(
    missing.map((resource) => {
      const payload = {
        ...mapResourceSetToEditPayload(resource.resource_set_id, resource),
        symbology: EPA_GULF_SYMBOLOGY,
      };
      const { members: _members, ...dataForBackend } = payload;
      return axios.put(
        `${config.apiGateway.ics_resource_sets}/${resource.resource_set_id}`,
        {
          ...camelToSnakeCase(dataForBackend),
          user_guid: userGuid,
          group_guid: groupGuid,
          incident_id: null,
        }
      );
    })
  );
  return true;
}

async function ensureEpaGulfResourceSetsForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const resp = await axios.get<ResourceSet[]>(
    `${config.apiGateway.ics_resource_sets}/${groupGuid}`
  );
  const existing = resp.data ?? [];
  const existingMarkers = new Set(
    existing
      .filter(isEpaGulfResourceSet)
      .map((resource) => resource.notes as string)
  );

  const sites = await queryNplSitesInHeleneCone();
  const toCreate = sites.filter(
    (facility) => !existingMarkers.has(epaGulfResourceNotesMarker(facility.id))
  );

  let didMutate = false;

  if (toCreate.length > 0) {
    const CREATE_BATCH = 15;
    for (let i = 0; i < toCreate.length; i += CREATE_BATCH) {
      const batch = toCreate.slice(i, i + CREATE_BATCH);
      await Promise.all(
        batch.map((facility) => {
          const notes = epaGulfResourceNotesMarker(facility.id);
          return axios.post(config.apiGateway.ics_resource_sets, {
            name: formatEpaFacilityLabel(facility),
            description: `${formatEpaFacilityLabel(facility)} — NPL Superfund site inside the Helene forecast cone.`,
            kind: EPA_RESOURCE_KIND,
            type: EPA_RESOURCE_TYPE,
            quantity: 1,
            status: 'In Organization',
            asset_status: 'PMC',
            asset_status_timestamp: new Date().toISOString(),
            address: `${facility.city}, ${facility.state}`,
            symbology: EPA_GULF_SYMBOLOGY,
            notes,
            geometry: {
              type: 'Point',
              coordinates: [facility.lon, facility.lat],
            },
            incidents: [],
            archived: false,
            group_guid: groupGuid,
            user_guid: userGuid,
            incident_id: null,
          });
        })
      );
    }
    didMutate = true;
  }

  const patched = await patchMissingEpaSymbology({
    resources: existing,
    groupGuid,
    userGuid,
  });
  if (patched) didMutate = true;

  if (didMutate) {
    await queryClient.invalidateQueries({
      queryKey: resourceSetKeys.all,
    });
  }
}

export function useEnsureEpaGulfResourceSets() {
  const epaEnabled = useEpaFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!epaEnabled || !groupGuid || !userGuid) {
      return;
    }

    const scopeKey = `${groupGuid}:${userGuid}`;
    if (completedRef.current === scopeKey) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(scopeKey);
    if (!ensurePromise) {
      ensurePromise = ensureEpaGulfResourceSetsForGroup({
        groupGuid,
        userGuid,
        queryClient,
      })
        .catch((error) => {
          console.error('[EPA] Failed to ensure Gulf resource sets', error);
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(scopeKey);
        });
      ensureLocks.set(scopeKey, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = scopeKey;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [epaEnabled, groupGuid, userGuid, queryClient]);
}
